import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMonitor, FiWifi, FiSettings, FiPlay, FiGrid } = FiIcons;

const AndroidTVApp = () => {
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // disconnected, connecting, connected
  const [pairingCode, setPairingCode] = useState('');

  const handleConnect = () => {
    if (!pairingCode) return;
    setConnectionStatus('connecting');
    
    // Simulate connection process
    setTimeout(() => {
      setConnectionStatus('connected');
    }, 3000);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-8">
      <div className="max-w-2xl w-full text-center">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <div className="w-24 h-24 bg-gradient-to-br from-purple-400 to-pink-400 rounded-3xl flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon={FiMonitor} className="text-white text-4xl" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">SignageCloud Player</h1>
          <p className="text-xl text-white/70">
            Connect your Android TV to your SignageCloud dashboard
          </p>
        </motion.div>

        {connectionStatus === 'disconnected' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-3xl p-12"
          >
            <div className="mb-8">
              <SafeIcon icon={FiWifi} className="text-6xl text-white/50 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-white mb-4">Connect to Dashboard</h2>
              <p className="text-white/70 mb-8">
                Enter the pairing code from your SignageCloud dashboard to connect this display
              </p>
            </div>
            <div className="space-y-6">
              <div>
                <input
                  type="text"
                  value={pairingCode}
                  onChange={(e) => setPairingCode(e.target.value.toUpperCase())}
                  placeholder="Enter 6-digit pairing code"
                  className="w-full px-6 py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl text-white text-center text-2xl font-mono placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent tracking-widest"
                  maxLength="6"
                />
              </div>
              <button
                onClick={handleConnect}
                disabled={pairingCode.length !== 6}
                className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl font-semibold text-xl hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
              >
                Connect Display
              </button>
            </div>
            
            {/* Multi-Screen Sync Option */}
            <div className="mt-8 pt-8 border-t border-white/20">
              <Link 
                to="/tv/sync-setup"
                className="flex items-center justify-center space-x-2 px-6 py-3 bg-white/10 border border-white/20 text-white rounded-xl hover:bg-white/20 transition-colors"
              >
                <SafeIcon icon={FiGrid} />
                <span>Multi-Screen Sync Setup</span>
              </Link>
              <p className="text-white/60 text-sm mt-4">
                Set up this display as part of a synchronized multi-screen video wall
              </p>
            </div>
          </motion.div>
        )}

        {connectionStatus === 'connecting' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-3xl p-12"
          >
            <div className="animate-spin w-16 h-16 border-4 border-purple-400 border-t-transparent rounded-full mx-auto mb-8"></div>
            <h2 className="text-2xl font-bold text-white mb-4">Connecting...</h2>
            <p className="text-white/70">
              Establishing connection to SignageCloud dashboard
            </p>
            <div className="mt-8 bg-white/5 rounded-2xl p-4">
              <p className="text-white/60 text-sm">Pairing Code: <span className="font-mono text-white">{pairingCode}</span></p>
            </div>
          </motion.div>
        )}

        {connectionStatus === 'connected' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-3xl p-12"
          >
            <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-emerald-400 rounded-full flex items-center justify-center mx-auto mb-8">
              <SafeIcon icon={FiWifi} className="text-white text-2xl" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">Connected Successfully!</h2>
            <p className="text-white/70 mb-8">
              Your display is now connected to SignageCloud. Content will begin playing shortly.
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              <div className="bg-white/5 rounded-2xl p-6">
                <h3 className="text-white font-medium mb-2">Display Status</h3>
                <p className="text-green-400">Online & Ready</p>
              </div>
              <div className="bg-white/5 rounded-2xl p-6">
                <h3 className="text-white font-medium mb-2">Current Playlist</h3>
                <p className="text-white/70">Welcome Messages</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/tv/player/lobby-display"
                className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-300"
              >
                <SafeIcon icon={FiPlay} />
                <span>Start Player</span>
              </Link>
              <Link
                to="/tv/sync-setup"
                className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 bg-white/10 border border-white/20 text-white rounded-xl hover:bg-white/20 transition-colors"
              >
                <SafeIcon icon={FiGrid} />
                <span>Multi-Screen Setup</span>
              </Link>
              <Link
                to="/tv/setup"
                className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 bg-white/10 border border-white/20 text-white rounded-xl hover:bg-white/20 transition-colors"
              >
                <SafeIcon icon={FiSettings} />
                <span>Settings</span>
              </Link>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AndroidTVApp;